import { ContentType, State, TableBlock, TextBlock } from "../../types";
import { extractTableCells } from "../getDefaultState";

export type AddCorrectionsAction = Readonly<{
  blockId: string;
  correction: string;
  type: "ADD CORRECTIONS";
}>;

export const addCorrections = (state: State, action: AddCorrectionsAction): State => {
  if (!(action.blockId in state.blocks)) {
    return state;
  }
  const block = state.blocks[action.blockId];
  switch (block.type) {
    case ContentType.Table:
    case ContentType.Text: {
      const newElement = block.element.cloneNode() as Element;
      newElement.innerHTML = action.correction;
      if (block.type === ContentType.Table) {
        const newTableBlock: TableBlock = {
          ...block,
          element: newElement,
          text: action.correction,
        };
        // extract the new cells
        const cells = extractTableCells(newTableBlock);
        const blocks = {
          ...state.blocks,
          [action.blockId]: {
            ...newTableBlock,
            cells: cells.map(r => r.map(c => c.id)),
          },
        };
        // delete the old keys
        const oldCellKeys = Object.keys(blocks).filter(k => k.startsWith(`${newTableBlock.id}-`));
        for (const oldCellKey of oldCellKeys) {
          delete blocks[oldCellKey];
        }
        const page = {
          ...state.pages[newTableBlock.page],
          blocks: state.pages[newTableBlock.page].blocks.filter(b => b in blocks),
        };
        // add the keys to the list of blocks and blocks on the page
        for (const row of cells) {
          for (const cell of row) {
            blocks[cell.id] = cell;
            page.blocks.push(cell.id);
          }
        }
        return {
          ...state,
          blocks,
          modified: true,
          // remove any missing cell ids that may have been removed
          pages: [
            ...state.pages.slice(0, newTableBlock.page),
            page,
            ...state.pages.slice(newTableBlock.page + 1),
          ],
          // remove any missing cell ids that may have been removed
          values: Object.keys(state.values).reduce((prev, next) => {
            const idRef = prev[next].idRef;
            if (idRef !== null && !(idRef in blocks)) {
              if (prev === state.values) {
                return {
                  ...prev,
                  [next]: {
                    ...prev[next],
                    idRef: null,
                  },
                };
              }
              prev[next] = { ...prev[next], idRef: null };
            }
            return prev;
          }, state.values),
        };
      }
      const newTextBlock: TextBlock = { ...block, element: newElement, text: action.correction };
      return {
        ...state,
        blocks: {
          ...state.blocks,
          [action.blockId]: newTextBlock,
        },
        modified: true,
      };
    }
    case ContentType.Image:
    case ContentType.Math:
    case ContentType.TableCell:
      return state;
  }
};
